import type { ReadonlySignal } from '@preact/signals-react';
import type { KnownColumnTemplateKey } from '@thinkalpha/ui-types/contracts/column-template';
import type { KeysWithoutReturnObjects } from 'i18next';
import type { IsBadAccountIdentifiers } from 'src/components/menus/AccountsMenu/state/types';
import type { HelpDocumentId } from 'src/features/help/content/types';
import type { IdeaPlanTypes } from 'src/features/widget/components/WidgetHeader/WidgetHeaderMenus/IdeasMenus';
import type {
    // Signals
    BacktestEditorCapabilitySignals,
    BacktestEditorActionEvents,
    EventsGoToDateSignal,
    EventsEventTypeFilterSignal,
    NameSignal,
    IsMaximizedSignal,
    ChannelSignal,
    ContainerSignal,
    ContainerActiveTabSignal,
    TabsByIdSignal,
    StickyFilterValueSignal,
    IsStickyFilterStickySignal,
    ConnectionStatusSignal,
    SelectedAccountIdsSignal,
    InstrumentSignal,
    AllowedInstrumentTypesSignal,
    OrderStatusFilterSignal,
    BlotterTodayOnlySignal,
    HideZeroPositionsSignal,
    IdeasSignals,
    IdeaEditorCapabilitySignals,
    SelectedColumnTemplateSignal,
    TableRowCountSignal,
    TableTotalRowCountSignal,
    IsColumnTemplateLibraryOpenSignal,
    FormattableGridSettingsSignal,
    IsValidationModeEnabledSignal,
    IsScrollingEnabledSignal,
    SelectedPlatformsSignal,
    SectionVisibilitySignal,
    TradeProSettingsSignals,
    ColumnOptionsSignal,
    LocatesSettingsSignals,
    SymbolRetentionSignal,
    // Events
    DeleteWidgetEvent,
    ToggleMaximizedEvent,
    ChannelChangeEvent,
    ResetChannelDataEvent,
    ChangeTabEvent,
    DeleteTabEvent,
    ChangeTabNameEvent,
    UpdateStickyFilterEvent,
    ToggleStickyFilterEvent,
    UpdateSelectedAccountIdsEvent,
    SetIsAccountSelectionValidEvent,
    InstrumentChangeEvent,
    ChangeOrderStatusFilterEvent,
    ToggleBlotterTodayOnlyEvent,
    ToggleHideZeroPositionsEvent,
    IdeasEvents,
    IdeaEditorActionEvents,
    SelectColumnTemplateEvent,
    OpenColumnTemplateLibraryEvent,
    ExportCSVDownloadEvent,
    OpenManualReviewStandaloneEvent,
    ToggleValidationModeEvent,
    ToggleScrollingEvent,
    UpdateSelectedPlatformsEvent,
    ToggleSectionVisibilityEvent,
    TradeProSettingsEvents,
    LocatesSettingsEvents,
    IndicatorSelectedEvent,
    SelectedFastIdeasSignal,
    ColumnOptionsEvents,
    UpdateSymbolRetentionEvent,
    ManualReviewAlertsIsEnabledSignal,
    ToggleManualReviewAlertsIsEnabledEvent,
    AlertsEnabledSignal,
    ToggleAlertsEnabledEvent,
    SymbolFilterSignal,
    UpdateSymbolFilterEvent,
    ActiveAlertsTabSignal,
    ChangeAlertsTabEvent,
    AddIdeaAlertEvent,
    SlowIdeaSignal,
    SelectedSlowIdeasSignal,
    TableWidgetSignals,
    TableWidgetEvents,
    EventsGoToDateChangeEvent,
    EventsEventTypeFilterChangeEvent,
    FilingsGoToDateSignal,
    FilingsSelectedFilingTypesSignal,
    FilingsAvailableFilingTypesSignal,
    FilingsSelectedFilingTagsSignal,
    FilingsAvailableFilingTagsSignal,
    FilingsGoToDateChangeEvent,
    FilingsFilingTypeFilterChangeEvent,
    FilingsFilingTagsFilterChangeEvent,
    BacktestNameSignal,
    BacktestSetNameEvent,
    BacktestSwitchToListEvent,
    BacktestSwitchToWizardEvent,
    BacktestViewModeSignal,
    BasketTradingNameSignal,
    BasketTradingSetNameEvent,
    BasketTradingEditorCapabilitySignals,
    BasketTradingEditorActionEvents,
    BasketTradingViewModeSignal,
    BasketTradingSetViewModeEvent,
    GridSettingsDialogOpenEvent,
    IsGridSettingsDialogOpenSignal,
} from 'src/types/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';

type WidgetHeaderElementBase = {
    key?: string; // used for header elements that render multiple versions
    position: number;
    // If no overflow position is provided, position is then used to determine order of elements in the overflow dropdown
    overflowPosition?: number;
    /** When true, the element is only rendered in the Electron desktop app. Defaults to false (shown on both web and desktop). */
    desktopOnly?: boolean;
};

// Visibility union (priority vs alwaysVisible vs overflowOnly)
type WidgetHeaderVisibility =
    | { priority: number; alwaysVisible?: undefined; overflowOnly?: undefined }
    | { alwaysVisible: true; priority?: never; overflowOnly?: undefined }
    | { overflowOnly: true; priority?: never; alwaysVisible?: undefined };

// Menu option types for ideas menu
export type IdeasMenuOptionType = 'save' | 'saveAs' | 'createNew';

/**
 * Type constructor for widget header elements.
 * Attaches phantom type markers for required signals and events.
 *
 * @template TProps - The element-specific props including `type`
 * @template TRequiredSignals - Signal types the element requires from the event bus
 * @template TRequiredEvents - Event types the element requires from the event bus
 */
type WidgetHeaderElementConstructor<
    TProps extends { type: string },
    TRequiredSignals = object,
    TRequiredEvents = object,
> = WidgetHeaderElementBase &
    WidgetHeaderVisibility &
    TProps & {
        /** @internal Phantom type marker for required signals */
        readonly __signals?: TRequiredSignals;
        /** @internal Phantom type marker for required events */
        readonly __events?: TRequiredEvents;
    };

// Window Management Elements

export type OverflowDropdownElement = WidgetHeaderElementConstructor<{ type: 'overflowDropdown' }>;

export type NameOverflowMenuElement = WidgetHeaderElementConstructor<{ type: 'nameOverflowMenu' }, NameSignal>;

export type MinimizeElement = WidgetHeaderElementConstructor<{ type: 'minimize' }>;

export type MaximizeElement = WidgetHeaderElementConstructor<
    { type: 'maximize' },
    IsMaximizedSignal,
    ToggleMaximizedEvent
>;

export type DeleteElement = WidgetHeaderElementConstructor<{ type: 'delete' }, object, DeleteWidgetEvent>;

export type TabsElement = WidgetHeaderElementConstructor<
    { type: 'tabs'; modifiable?: boolean },
    ContainerSignal & ContainerActiveTabSignal & TabsByIdSignal,
    ChangeTabEvent & DeleteTabEvent & ChangeTabNameEvent
>;

export type WidgetNameElement = WidgetHeaderElementConstructor<{ type: 'widgetName' }, NameSignal>;

export type WidgetNameIdeasElement = WidgetHeaderElementConstructor<
    { type: 'widgetNameIdeas' },
    NameSignal & SelectedFastIdeasSignal<IdeaPlanTypes>
>;

// Utility Elements

export type SpacerElement = WidgetHeaderElementConstructor<{ type: 'spacer' }>;

export type LocalOnlyInfoElement = WidgetHeaderElementConstructor<{ type: 'localOnlyInfo' }>;

// Help Element

export type HelpDocumentElement = WidgetHeaderElementConstructor<{ type: 'helpDocument'; documentId: HelpDocumentId }>;

// Linking Elements

export type ChannelPickerElement = WidgetHeaderElementConstructor<
    { type: 'channelPicker' },
    ChannelSignal,
    ChannelChangeEvent & ResetChannelDataEvent
>;

// Widget-specific Elements

export type StickyFilterElement = WidgetHeaderElementConstructor<
    { type: 'stickyFilter'; initialExpanded?: boolean; wide?: boolean },
    StickyFilterValueSignal & IsStickyFilterStickySignal,
    UpdateStickyFilterEvent & ToggleStickyFilterEvent
>;

export type GridSettingsDialogElement = WidgetHeaderElementConstructor<
    { type: 'gridSettingsDialog' },
    IsGridSettingsDialogOpenSignal,
    GridSettingsDialogOpenEvent
>;

export type ConnectionStatusElement = WidgetHeaderElementConstructor<
    { type: 'connectionStatus' },
    ConnectionStatusSignal,
    never
>;

export type AccountsElement = WidgetHeaderElementConstructor<
    {
        type: 'accounts';
        selectType?: 'single' | 'multi';
        allAccountsLabel?: 'all-accounts' | 'my-activity';
        disabledAccountIdentifiers?: IsBadAccountIdentifiers;
        disabledAccountIds?: string[];
    },
    SelectedAccountIdsSignal,
    UpdateSelectedAccountIdsEvent & SetIsAccountSelectionValidEvent
>;

export type SymbolPickerPlaceholderKey =
    Extract<
        KeysWithoutReturnObjects['json'],
        `widgets::header::symbol-placeholder::${string}`
    > extends `widgets::header::symbol-placeholder::${infer T}`
        ? T
        : never;

export type SymbolPickerElement = WidgetHeaderElementConstructor<
    {
        type: 'symbolPicker';
        clearable?: boolean;
        resetOnChange?: boolean;
        variant?: 'light';
        placeholder?: SymbolPickerPlaceholderKey;
    },
    InstrumentSignal & AllowedInstrumentTypesSignal,
    InstrumentChangeEvent
>;

// Blotter Elements

export type BlotterStatusFilterElement = WidgetHeaderElementConstructor<
    { type: 'blotterStatusFilter' },
    OrderStatusFilterSignal,
    ChangeOrderStatusFilterEvent
>;

export type BlotterTodayOnlyElement = WidgetHeaderElementConstructor<
    { type: 'blotterTodayOnly' },
    BlotterTodayOnlySignal,
    ToggleBlotterTodayOnlyEvent
>;

// Positions Elements

export type HideZeroPositionsElement = WidgetHeaderElementConstructor<
    { type: 'hideZeroPositions' },
    HideZeroPositionsSignal,
    ToggleHideZeroPositionsEvent
>;

// Results & Ideas Elements

/**
 * Ideas menu element for selecting and managing ideas (read-only, no editor support).
 */
export type IdeasMenuElement = WidgetHeaderElementConstructor<
    {
        type: 'ideasMenu';
        selectType: 'single' | 'multi';
        ideaType: 'fast' | 'slow';
        ideaPlanTypes: IdeaPlanTypes[];
        showSelected: boolean;
        allowDelete: boolean;
    },
    SlowIdeaSignal & SelectedFastIdeasSignal<IdeaPlanTypes> & SelectedSlowIdeasSignal<IdeaPlanTypes>,
    IdeasEvents
>;

/**
 * Ideas menu element with editor support for save/saveAs/createNew operations.
 * Requires IdeaEditorCapabilitySignals and IdeaEditorActionEvents.
 */
export type IdeasMenuEditorElement = WidgetHeaderElementConstructor<
    {
        type: 'ideasMenuEditor';
        selectType: 'single' | 'multi';
        ideaType: 'fast' | 'slow';
        ideaPlanTypes: IdeaPlanTypes[];
        showSelected: boolean;
        allowDelete: boolean;
        /**
         * Array of menu options to show in the ideas menu.
         * These render as additional menu items (e.g., Save, Save a Copy, Create New).
         */
        menuOptions: IdeasMenuOptionType[];
        /**
         * Label to show when no idea is selected (e.g., for unsaved ideas).
         */
        noSelectionLabel?: string;
    },
    IdeasSignals<IdeaPlanTypes> & IdeaEditorCapabilitySignals,
    IdeasEvents & IdeaEditorActionEvents
>;

// Ideas Menu V2 Elements

/** Menu item types available in the ideas overflow menu */
export type IdeasMenuItemType = 'new' | 'open' | 'save' | 'copy' | 'delete';

/**
 * Ideas menu items rendered exclusively in the nameOverflowMenu.
 * Uses `overflowOnly: true` — never measured or rendered in the header bar.
 * Renders a group of MenuItem items (New, Open, Save, Save a Copy, Delete).
 */
export type IdeasMenuItemsElement = WidgetHeaderElementConstructor<
    {
        type: 'ideasMenuItems';
        items: IdeasMenuItemType[];
        ideaType: 'fast' | 'slow';
        ideaPlanTypes: IdeaPlanTypes[];
        selectType: 'single' | 'multi';
        showSelected: boolean;
        allowDelete: boolean;
    },
    IdeasSignals<IdeaPlanTypes> & IdeaEditorCapabilitySignals,
    IdeasEvents & IdeaEditorActionEvents
>;

export type ColumnTemplateMenuElement = WidgetHeaderElementConstructor<
    { type: 'columnTemplateMenu'; columnTemplateKey: KnownColumnTemplateKey; alwaysUseGridSettings?: boolean },
    SelectedColumnTemplateSignal & IsColumnTemplateLibraryOpenSignal,
    SelectColumnTemplateEvent & OpenColumnTemplateLibraryEvent & GridSettingsDialogOpenEvent
>;

export type TableRowCountElement = WidgetHeaderElementConstructor<{ type: 'tableRowCount' }, TableRowCountSignal>;

export type TableRowCountWithTotalElement = WidgetHeaderElementConstructor<
    { type: 'tableRowCountWithTotal' },
    TableRowCountSignal & TableTotalRowCountSignal
>;

export type ExportCSVButtonElement = WidgetHeaderElementConstructor<
    { type: 'exportCSVButton' },
    object,
    ExportCSVDownloadEvent
>;

export type OpenManualReviewStandaloneElement = WidgetHeaderElementConstructor<
    { type: 'openManualReviewStandalone' },
    object,
    OpenManualReviewStandaloneEvent
>;

// Settings Elements

export type SettingsMenuElement = WidgetHeaderElementConstructor<
    { type: 'settingsMenu' },
    IsValidationModeEnabledSignal & IsScrollingEnabledSignal & FormattableGridSettingsSignal & ColumnOptionsSignal,
    ToggleValidationModeEvent & ToggleScrollingEvent & ColumnOptionsEvents
>;

export type SettingsMenuNoScrollingElement = WidgetHeaderElementConstructor<
    { type: 'settingsMenuNoScrolling' },
    IsValidationModeEnabledSignal & FormattableGridSettingsSignal & ColumnOptionsSignal,
    ToggleValidationModeEvent & ColumnOptionsEvents
>;

// Platforms Element (for Accounts widget)

export type PlatformsElement = WidgetHeaderElementConstructor<
    { type: 'platforms' },
    SelectedPlatformsSignal,
    UpdateSelectedPlatformsEvent
>;

// Section View Elements (for Scanner, Screener, SearchAlpha)

export type SectionViewMenuElement = WidgetHeaderElementConstructor<
    { type: 'sectionViewMenu'; viewConfig: { id: string; label: string }[] },
    SectionVisibilitySignal,
    ToggleSectionVisibilityEvent
>;

// TradePro Elements

export type TradeProSettingsMenuElement = WidgetHeaderElementConstructor<
    { type: 'tradeProSettingsMenu' },
    TradeProSettingsSignals,
    TradeProSettingsEvents
>;

export type TradeProCsvUploadElement = WidgetHeaderElementConstructor<{ type: 'tradeProCsvUpload' }, object, object>;

// Locates Elements

export type LocatesSettingsMenuElement = WidgetHeaderElementConstructor<
    { type: 'locatesSettingsMenu' },
    LocatesSettingsSignals,
    LocatesSettingsEvents
>;

// Indicator Library Element (for SearchAlpha)

export type IndicatorLibraryElement = WidgetHeaderElementConstructor<
    { type: 'indicatorLibrary' },
    object,
    IndicatorSelectedEvent
>;

// Column Options Elements

export type ColumnOptionsElement = WidgetHeaderElementConstructor<
    { type: 'columnOptions' },
    ColumnOptionsSignal,
    ColumnOptionsEvents
>;

// Symbol Retention Element (for News, Events, Filings widgets)

export type SymbolRetentionElement = WidgetHeaderElementConstructor<
    { type: 'symbolRetention' },
    SymbolRetentionSignal,
    UpdateSymbolRetentionEvent
>;

export type ManualReviewAlertsSwitchElement = WidgetHeaderElementConstructor<
    { type: 'manualReviewAlertsSwitch' },
    ManualReviewAlertsIsEnabledSignal,
    ToggleManualReviewAlertsIsEnabledEvent
>;

export type AlertsEnabledToggleElement = WidgetHeaderElementConstructor<
    { type: 'alertsEnabledToggle' },
    AlertsEnabledSignal,
    ToggleAlertsEnabledEvent
>;

export type AlertsTabSwitcherElement = WidgetHeaderElementConstructor<
    { type: 'alertsTabSwitcher' },
    ActiveAlertsTabSignal,
    ChangeAlertsTabEvent
>;

export type AlertsAddIdeaAlertElement = WidgetHeaderElementConstructor<
    { type: 'alertsAddIdeaAlert' },
    object,
    AddIdeaAlertEvent
>;

export type SymbolFilterElement = WidgetHeaderElementConstructor<
    { type: 'symbolFilter' },
    SymbolFilterSignal,
    UpdateSymbolFilterEvent
>;

// Backtest widget header elements

/** Menu item types available in the backtest overflow menu */
export type BacktestMenuItemType = 'new' | 'open' | 'save' | 'copy' | 'delete' | 'allBacktests';

/**
 * Backtest menu items rendered exclusively in the nameOverflowMenu.
 * Uses `overflowOnly: true` — never measured or rendered in the header bar.
 * Renders a group of MenuItem items (New, Open, Save, Save a Copy, Delete).
 */
export type BacktestMenuItemsElement = WidgetHeaderElementConstructor<
    {
        type: 'backtestMenuItems';
        items: BacktestMenuItemType[];
    },
    BacktestEditorCapabilitySignals & BacktestNameSignal & NameSignal & BacktestViewModeSignal,
    BacktestEditorActionEvents & BacktestSwitchToListEvent
>;

/**
 * Inline-editable backtest name. Click to rename, Enter to confirm.
 * Shows a "Rename" tooltip on hover.
 */
export type BacktestNameElement = WidgetHeaderElementConstructor<
    { type: 'backtestName' },
    BacktestNameSignal,
    BacktestSetNameEvent
>;

/**
 * Floppy-disk save icon. Bright/active when backtest is dirty, dimmed when clean.
 * Triggers backtest::editorSave on click.
 */
export type BacktestSaveElement = WidgetHeaderElementConstructor<
    { type: 'backtestSave' },
    BacktestEditorCapabilitySignals,
    BacktestEditorActionEvents
>;

/**
 * "Edit →" button shown in the header when viewing backtest results.
 * Only visible when viewMode is 'results'. Switches back to wizard mode.
 */
export type BacktestEditElement = WidgetHeaderElementConstructor<
    { type: 'backtestEdit' },
    BacktestViewModeSignal,
    BacktestSwitchToWizardEvent
>;

// Basket Trading widget header elements
export type BasketTradingMenuItemType = 'new' | 'open' | 'save' | 'copy' | 'delete' | 'allBaskets';

export type BasketTradingNameElement = WidgetHeaderElementConstructor<
    { type: 'basketTradingName' },
    BasketTradingNameSignal,
    BasketTradingSetNameEvent
>;

export type BasketTradingSaveElement = WidgetHeaderElementConstructor<
    { type: 'basketTradingSave' },
    BasketTradingEditorCapabilitySignals,
    BasketTradingEditorActionEvents
>;

export type BasketTradingMenuItemsElement = WidgetHeaderElementConstructor<
    {
        type: 'basketTradingMenuItems';
        items: BasketTradingMenuItemType[];
    },
    BasketTradingEditorCapabilitySignals & BasketTradingNameSignal & NameSignal & BasketTradingViewModeSignal,
    BasketTradingEditorActionEvents & BasketTradingSetViewModeEvent
>;

export type TableWidgetSettingsElement = WidgetHeaderElementConstructor<
    { type: 'tableWidgetSettings' },
    TableWidgetSignals,
    TableWidgetEvents
>;

// ============================================================================
// Events Widget Second-Row Elements
//
// ⚠️  WARNING: These elements are EXCLUSIVELY for the Events widget's second
// control row. They are NOT styled for standard widget headers and MUST NOT
// be added to any other widget's headerElements array. The visual design of
// these elements intentionally differs from regular header elements — adding
// them elsewhere will produce visually incorrect results.
// ============================================================================

/**
 * "Go to date" element — Events widget second control row only.
 *
 * ⚠️  NOT FOR STANDARD WIDGET HEADERS. See module comment above.
 */
export type EventsGoToDateElement = WidgetHeaderElementConstructor<
    { type: 'eventsGoToDate' },
    EventsGoToDateSignal,
    EventsGoToDateChangeEvent
>;

/**
 * Event type filter element — Events widget second control row only.
 *
 * ⚠️  NOT FOR STANDARD WIDGET HEADERS. See module comment above.
 */
export type EventsEventTypeFilterElement = WidgetHeaderElementConstructor<
    { type: 'eventsEventTypeFilter' },
    EventsEventTypeFilterSignal,
    EventsEventTypeFilterChangeEvent
>;

export type EventsCalendarToggleElement = WidgetHeaderElementConstructor<{ type: 'eventsCalendarToggle' }>;

// ============================================================================
// EDGAR/Filings Widget Second-Row Elements
// These elements are for the Filings widget's second control row ONLY and must not
// be added to any other widget's headerElements array.
// ============================================================================

/**
 * "Go to date" element — Filings widget second control row only.
 *
 * ⚠️  NOT FOR STANDARD WIDGET HEADERS. See module comment above.
 */
export type FilingsGoToDateElement = WidgetHeaderElementConstructor<
    { type: 'filingsGoToDate' },
    FilingsGoToDateSignal,
    FilingsGoToDateChangeEvent
>;

/**
 * Filing type filter element — Filings widget second control row only.
 *
 * ⚠️  NOT FOR STANDARD WIDGET HEADERS. See module comment above.
 */
export type FilingsFilingTypeFilterElement = WidgetHeaderElementConstructor<
    { type: 'filingsFilingTypeFilter' },
    FilingsSelectedFilingTypesSignal & FilingsAvailableFilingTypesSignal,
    FilingsFilingTypeFilterChangeEvent
>;

/**
 * Filing tags filter element — Filings widget second control row only.
 * Renders null when '8-K' is not in the selected filing types.
 *
 * ⚠️  NOT FOR STANDARD WIDGET HEADERS. See module comment above.
 */
export type FilingsFilingTagsFilterElement = WidgetHeaderElementConstructor<
    { type: 'filingsFilingTagsFilter' },
    FilingsSelectedFilingTagsSignal & FilingsAvailableFilingTagsSignal & FilingsSelectedFilingTypesSignal,
    FilingsFilingTagsFilterChangeEvent
>;

/**
 * Configuration for element groups with spacing.
 * Groups allow multiple header elements to be treated as a unit for spacing purposes.
 */
export type WidgetHeaderElementGroup = {
    id: string;
    memberElements: WidgetHeaderElementType[];
    spacing: {
        /** Width of spacers in pixels */
        left?: number;
        right?: number;
        /** Width of spacers between members within the same group, in pixels */
        inner?: number;
    };
};

export type WidgetHeaderElement =
    | OverflowDropdownElement
    | NameOverflowMenuElement
    | MinimizeElement
    | MaximizeElement
    | DeleteElement
    | TabsElement
    | WidgetNameElement
    | SpacerElement
    | ChannelPickerElement
    | StickyFilterElement
    | GridSettingsDialogElement
    | ConnectionStatusElement
    | AccountsElement
    | SymbolPickerElement
    | BlotterStatusFilterElement
    | BlotterTodayOnlyElement
    | HideZeroPositionsElement
    | IdeasMenuElement
    | IdeasMenuEditorElement
    | IdeasMenuItemsElement
    | ColumnTemplateMenuElement
    | TableRowCountElement
    | TableRowCountWithTotalElement
    | ExportCSVButtonElement
    | SettingsMenuElement
    | SettingsMenuNoScrollingElement
    | PlatformsElement
    | SectionViewMenuElement
    | TradeProSettingsMenuElement
    | TradeProCsvUploadElement
    | LocatesSettingsMenuElement
    | IndicatorLibraryElement
    | WidgetNameIdeasElement
    | ColumnOptionsElement
    | OpenManualReviewStandaloneElement
    | SymbolRetentionElement
    | ManualReviewAlertsSwitchElement
    | AlertsEnabledToggleElement
    | AlertsTabSwitcherElement
    | AlertsAddIdeaAlertElement
    | SymbolFilterElement
    | TableWidgetSettingsElement
    | EventsGoToDateElement
    | EventsEventTypeFilterElement
    | EventsCalendarToggleElement
    | FilingsGoToDateElement
    | FilingsFilingTypeFilterElement
    | FilingsFilingTagsFilterElement
    | BacktestMenuItemsElement
    | BacktestNameElement
    | BacktestSaveElement
    | BacktestEditElement
    | BasketTradingNameElement
    | BasketTradingSaveElement
    | BasketTradingMenuItemsElement
    | LocalOnlyInfoElement
    | HelpDocumentElement;

/** Derived union of all valid header element type strings */
export type WidgetHeaderElementType = WidgetHeaderElement['type'];

export interface WidgetHeaderGroupSpacer {
    type: 'groupSpacer';
    key: string;
    size: number;
}

/** Extract the phantom signal requirements from an element type */
type ExtractSignals<T> = T extends { __signals?: infer S } ? S : object;

/** Extract the phantom event requirements from an element type */
type ExtractEvents<T> = T extends { __events?: infer E } ? E : object;

/** Convert a union to an intersection */
type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

/**
 * Given an array of header elements, compute the intersection of all required signals.
 * Use this to verify that an EventBus provides all signals needed by the elements.
 */
export type RequiredSignalsFor<TElements extends readonly WidgetHeaderElement[]> = UnionToIntersection<
    ExtractSignals<TElements[number]>
>;

/**
 * Given an array of header elements, compute the intersection of all required events.
 * Use this to verify that an EventBus provides all events needed by the elements.
 */
export type RequiredEventsFor<TElements extends readonly WidgetHeaderElement[]> = UnionToIntersection<
    ExtractEvents<TElements[number]>
>;

/**
 * Given an EventBus type, compute which header element types are allowed.
 * An element is allowed if the EventBus's signals extend the element's required signals,
 * and the EventBus's events extend the element's required events.
 */
type AllowedElementsFor<TEventBus> =
    TEventBus extends EventBus<infer TSignals, infer TEvents>
        ? WidgetHeaderElement extends infer E
            ? E extends WidgetHeaderElement
                ? (ExtractSignals<E> extends object ? (TSignals extends ExtractSignals<E> ? E : never) : E) &
                      (ExtractEvents<E> extends object ? (TEvents extends ExtractEvents<E> ? E : never) : E)
                : never
            : never
        : never;

/**
 * Compute the required EventBus type for a given array of header elements.
 * Note: Due to TypeScript constraints, this uses type assertions internally.
 */
export type EventBusFor<TElements extends readonly WidgetHeaderElement[]> =
    RequiredSignalsFor<TElements> extends infer S
        ? RequiredEventsFor<TElements> extends infer E
            ? S extends Record<string, ReadonlySignal<unknown>>
                ? E extends Record<string, unknown>
                    ? EventBus<S, E>
                    : never
                : never
            : never
        : never;

/**
 * Validates that the provided header elements are compatible with the given EventBus type.
 * This is a passthrough function that performs compile-time validation only.
 *
 * @example
 * ```typescript
 * type MyWidgetEventBus = EventBus<MySignals, MyEvents>;
 *
 * const headerElements = makeHeaderElements<MyWidgetEventBus>([
 *   { type: 'delete', position: 100, priority: 10 },
 *   { type: 'ideasMenu', ... }, // Error if MyWidgetEventBus doesn't have IdeasSignals/IdeasEvents
 * ]);
 * ```
 */
export function makeHeaderElements<TEventBus extends EventBus<any, any>>(
    elements: AllowedElementsFor<TEventBus>[],
): AllowedElementsFor<TEventBus>[] {
    return elements;
}
